import React, { useCallback } from 'react';
import type { TerminalClause } from '@atlaskit/jql-ast';
import { useIntl } from '@atlassian/jira-intl';
import { DateTimeVariations } from '@atlassian/jira-jql-builder-basic-datetime/src/common/constants.tsx';
import type {
	DateTimeVariation,
	DateTimePickerValue,
} from '@atlassian/jira-jql-builder-basic-datetime/src/common/types.tsx';
import DateTimeView, {
	formatLabel as formatDateLabel,
	formatDateValueLabel,
	getJqlInfo,
	getTooltip,
	getValueFromClauses as getValueFromClausesForDate,
} from '@atlassian/jira-jql-builder-basic-datetime/src/ui/index.tsx';
import { DefaultPopupComponent } from '@atlassian/jira-jql-builder-basic-picker/src/ui/popup-container/index.tsx';
import { ConditionalTooltip } from '@atlassian/jira-jql-builder-common/src/ui/conditional-tooltip/index.tsx';
import getNewJqlFromDoubleClausePickerJqlData from '@atlassian/jira-jql-utils/src/utils/get-jql-from-jql-data.tsx';
import { JiraPopup as Popup } from '@atlassian/jira-popup/src/ui/jira-popup.tsx';
import type { OnSearchWrapper } from '../../../../common/types.tsx';
import PickerTrigger from '../../../../common/ui/picker-trigger/main.tsx';
import { useAST } from '../../../../controllers/ast/index.tsx';
import { useSendOptionAnalytics } from '../../../../utils/send-option-analytics.tsx';
import {
	useVerticalMode,
	useShouldRenderToParent,
} from '../../../../controllers/feature-context.tsx';
import { PickerTriggerLayout } from '../../../../common/ui/picker-trigger-layout/index.tsx';

type DatePickerWrapperProps = {
	fieldId: string;
	fieldLabel?: string;
	pickerType: DateTimeVariation;
	activePopupKey: string;
	clause: TerminalClause | undefined;
	secondaryClause: TerminalClause | undefined;
	onSearchWrapper?: OnSearchWrapper;
	onClear?: (onClearJqlClause: string | undefined) => void;
	onOpen: () => void;
	onClose: () => void;
	fieldDescription: string | null;
	isIrremovable?: boolean;
};

const getPickerTriggerContent = (
	verticalMode: boolean,
	fieldLabel: string,
	formatedDate: React.JSX.Element | null,
) => {
	if (verticalMode) {
		return <PickerTriggerLayout label={fieldLabel}>{formatedDate}</PickerTriggerLayout>;
	}

	return formatedDate;
};

export const DatePickerWrapper = ({
	activePopupKey,
	clause,
	secondaryClause,
	fieldId,
	fieldLabel,
	pickerType,
	onOpen,
	onClose,
	onClear,
	onSearchWrapper,
	fieldDescription,
	isIrremovable,
}: DatePickerWrapperProps): React.JSX.Element => {
	const intl = useIntl();
	const [ast] = useAST();
	const sendOptionAnalytics = useSendOptionAnalytics();
	const variation: DateTimeVariation = DateTimeVariations[pickerType] || 'DateTime';
	const dateRefinementValueFromJql = getValueFromClausesForDate(variation, clause, secondaryClause);
	const verticalMode = useVerticalMode();
	const tooltip = getTooltip({
		intl,
		label: fieldLabel || '',
		variation,
		value: dateRefinementValueFromJql,
	});
	const shouldRenderToParent = useShouldRenderToParent();
	const isOpen = activePopupKey === fieldId;

	const handleOnClick = useCallback(() => {
		isOpen ? onClose() : onOpen();
	}, [isOpen, onClose, onOpen]);

	const handleOnClear = useCallback(() => {
		onClear &&
			onClear(clause ? getNewJqlFromDoubleClausePickerJqlData(fieldId, ast, undefined) : undefined);
	}, [onClear, clause, fieldId, ast]);

	const formattedDate = verticalMode
		? formatDateValueLabel({
				intl,
				variation,
				value: dateRefinementValueFromJql,
			})
		: formatDateLabel({
				intl,
				label: fieldLabel || '',
				variation,
				value: dateRefinementValueFromJql,
			});

	return (
		<Popup
			key={fieldId}
			placement="bottom-start"
			isOpen={isOpen}
			onClose={onClose}
			popupComponent={DefaultPopupComponent}
			shouldRenderToParent={shouldRenderToParent}
			messageId="jql-builder-basic.ui.jql-editor.field.date-picker-wrapper.popup"
			messageType="transactional"
			trigger={({ ref }) => (
				<ConditionalTooltip
					condition={tooltip !== undefined}
					content={tooltip ?? null}
					position="top"
				>
					<PickerTrigger
						isInvalid={false}
						isSelected={isOpen}
						hasValues={Boolean(clause || secondaryClause)}
						onClick={handleOnClick}
						onClear={!isIrremovable ? handleOnClear : undefined}
						ref={ref}
						testId={`refinement-bar.filter-button.${fieldId}`}
						fieldDescription={fieldDescription}
						isIrremovable={isIrremovable}
					>
						{getPickerTriggerContent(verticalMode, fieldLabel || '', formattedDate)}
					</PickerTrigger>
				</ConditionalTooltip>
			)}
			content={() => (
				<DateTimeView
					fieldLabel={fieldLabel}
					variation={variation}
					value={dateRefinementValueFromJql}
					onSendOptionAnalytics={sendOptionAnalytics}
					onClosePopup={onClose}
					onChange={(value?: DateTimePickerValue) =>
						onSearchWrapper &&
						onSearchWrapper(
							getNewJqlFromDoubleClausePickerJqlData(fieldId, ast, getJqlInfo(value, variation)),
						)
					}
				/>
			)}
		/>
	);
};
